import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatCurrency, formatDateTime } from "@/lib/utils-format";
import { Plus, Package, PencilSimple, ClockCounterClockwise, Trash, TrendUp, TrendDown, Ticket, X as XIcon } from "@phosphor-icons/react";
import { FileUploader, fileUrl } from "@/components/FileUploader";

const CURRENCIES = ["EUR", "USD", "GBP", "INR"];

export default function Products() {
  const [products, setProducts] = useState([]);
  const [openForm, setOpenForm] = useState(null); // null | 'new' | {product}
  const [openHistory, setOpenHistory] = useState(null);

  const load = async () => setProducts((await api.get("/products")).data);
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Deactivate "${p.name}"? Past orders keep their prices; new orders can no longer pick this product.`)) return;
    try { await api.delete(`/products/${p.product_id}`); toast.success("Deactivated"); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-8" data-testid="products-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">Catalog</p>
          <h1 className="text-4xl font-black tracking-tighter">Products</h1>
          <p className="text-sm text-zinc-500 mt-2">Product + Packing + Shipping breakdown. Prices are logged every time you update them.</p>
        </div>
        <Button onClick={() => setOpenForm("new")} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="new-product-button">
          <Plus size={16} weight="bold" className="mr-1" /> New Product
        </Button>
      </div>

      <div className="border border-zinc-200 bg-white" data-testid="products-table">
        <table className="w-full data-table">
          <thead>
            <tr>
              <th className="text-left px-4 py-2.5"></th>
              <th className="text-left px-4 py-2.5">Name</th>
              <th className="text-left px-4 py-2.5">Unit</th>
              <th className="text-right px-4 py-2.5">Weight (kg)</th>
              <th className="text-right px-4 py-2.5">Product</th>
              <th className="text-right px-4 py-2.5">Packing</th>
              <th className="text-right px-4 py-2.5">Shipping</th>
              <th className="text-right px-4 py-2.5">Total</th>
              <th className="text-left px-4 py-2.5">Tiers</th>
              <th className="text-left px-4 py-2.5">Updated</th>
              <th className="text-right px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.product_id} className={`border-b border-zinc-100 hover:bg-zinc-50 ${!p.active ? "opacity-50" : ""}`} data-testid={`product-row-${p.product_id}`}>
                <td className="px-4 py-2">
                  {p.image_file_id ? (
                    <img
                      src={fileUrl(`/api/storage/files/${p.image_file_id}`)}
                      alt={p.name}
                      className="w-12 h-12 object-cover border border-zinc-200 rounded"
                      data-testid={`product-image-${p.product_id}`}
                    />
                  ) : (
                    <div className="w-12 h-12 border border-dashed border-zinc-200 bg-zinc-50 flex items-center justify-center text-zinc-400 rounded">
                      <Package size={16} />
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Package size={14} weight="bold" className="text-zinc-500" />
                    <span className="font-semibold text-sm">{p.name}</span>
                    {!p.active && <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-semibold bg-zinc-200 text-zinc-600 rounded">Inactive</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-zinc-600">{p.unit_size}</td>
                <td className="px-4 py-3 text-right font-mono text-xs">{p.weight_kg}</td>
                <td className="px-4 py-3 text-right font-mono text-sm">{formatCurrency(p.product_cost, p.currency)}</td>
                <td className="px-4 py-3 text-right font-mono text-sm">{formatCurrency(p.packing_cost, p.currency)}</td>
                <td className="px-4 py-3 text-right font-mono text-sm">{formatCurrency(p.shipping_cost, p.currency)}</td>
                <td className="px-4 py-3 text-right font-mono text-sm font-bold">{formatCurrency(p.total_price, p.currency)}</td>
                <td className="px-4 py-3 text-xs font-mono text-zinc-600">
                  {p.price_tiers?.length > 0
                    ? p.price_tiers.map((t, i) => (
                        <span key={i} className="inline-block mr-2 whitespace-nowrap">
                          <span className="text-zinc-400">{t.min_qty}+:</span> {formatCurrency(t.unit_price, p.currency)}
                        </span>
                      ))
                    : <span className="text-zinc-400">—</span>}
                </td>
                <td className="px-4 py-3 text-[11px] text-zinc-500 font-mono">{formatDateTime(p.updated_at)}<br /><span className="text-zinc-400">{p.last_updated_by}</span></td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setOpenHistory(p)} title="Price history" className="p-1 hover:bg-zinc-200 rounded" data-testid={`history-${p.product_id}`}><ClockCounterClockwise size={14} /></button>
                    <button onClick={() => setOpenForm({ product: p })} title="Edit" className="p-1 hover:bg-zinc-200 rounded" data-testid={`edit-${p.product_id}`}><PencilSimple size={14} /></button>
                    {p.active && <button onClick={() => remove(p)} title="Deactivate" className="p-1 hover:bg-red-50 text-zinc-400 hover:text-red-600 rounded" data-testid={`delete-${p.product_id}`}><Trash size={14} /></button>}
                  </div>
                </td>
              </tr>
            ))}
            {products.length === 0 && <tr><td colSpan={11} className="p-8 text-center text-sm text-zinc-500">No products yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {openForm && (
        <ProductForm
          product={openForm === "new" ? null : openForm.product}
          onClose={() => setOpenForm(null)}
          onSaved={() => { setOpenForm(null); load(); }}
        />
      )}
      {openHistory && <HistoryDialog product={openHistory} onClose={() => setOpenHistory(null)} />}
    </div>
  );
}

function ProductForm({ product, onClose, onSaved }) {
  const isEdit = !!product;
  const [form, setForm] = useState({
    name: product?.name || "",
    unit_size: product?.unit_size || "",
    weight_kg: product?.weight_kg || 0,
    product_cost: product?.product_cost || 0,
    packing_cost: product?.packing_cost || 0,
    shipping_cost: product?.shipping_cost || 0,
    currency: product?.currency || "EUR",
    bonus_threshold: product?.bonus_threshold || 0,
    bonus_quantity: product?.bonus_quantity || 0,
    price_tiers: product?.price_tiers?.length ? [...product.price_tiers] : [],
    image_file_id: product?.image_file_id || null,
    notes: product?.notes || "",
    change_note: "",
  });
  const [busy, setBusy] = useState(false);

  const total = (parseFloat(form.product_cost) || 0) + (parseFloat(form.packing_cost) || 0) + (parseFloat(form.shipping_cost) || 0);
  const original = isEdit ? product.total_price : 0;
  const delta = isEdit ? total - original : 0;
  const bonusEnabled = (parseInt(form.bonus_threshold, 10) || 0) > 0 && (parseInt(form.bonus_quantity, 10) || 0) > 0;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form,
        weight_kg: parseFloat(form.weight_kg) || 0,
        product_cost: parseFloat(form.product_cost) || 0,
        packing_cost: parseFloat(form.packing_cost) || 0,
        shipping_cost: parseFloat(form.shipping_cost) || 0,
        bonus_threshold: parseInt(form.bonus_threshold, 10) || 0,
        bonus_quantity: parseInt(form.bonus_quantity, 10) || 0,
        price_tiers: form.price_tiers
          .map((t) => ({ min_qty: parseInt(t.min_qty, 10) || 0, unit_price: parseFloat(t.unit_price) || 0 }))
          .filter((t) => t.min_qty > 0)
          .sort((a, b) => a.min_qty - b.min_qty),
      };
      if (isEdit) {
        await api.put(`/products/${product.product_id}`, payload);
        toast.success("Product updated");
      } else {
        delete payload.change_note;
        await api.post("/products", payload);
        toast.success("Product created");
      }
      onSaved();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
        <DialogHeader><DialogTitle>{isEdit ? `Edit ${product.name}` : "New Product"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="product-form">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Product Name *"><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-zinc-300 rounded-md" data-testid="product-name-input" /></Field>
            <Field label="Unit Size"><Input placeholder="0.500 kg bag" value={form.unit_size} onChange={(e) => setForm({ ...form, unit_size: e.target.value })} className="border-zinc-300 rounded-md" data-testid="product-unit-input" /></Field>
            <Field label="Weight (kg)"><Input type="number" step="0.01" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} className="border-zinc-300 rounded-md" data-testid="product-weight-input" /></Field>
            <Field label="Currency">
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger className="border-zinc-300 rounded-md" data-testid="product-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          <div className="border border-zinc-200 bg-zinc-50 p-4">
            <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">Cost Breakdown</p>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Product Cost *"><Input required type="number" step="0.01" value={form.product_cost} onChange={(e) => setForm({ ...form, product_cost: e.target.value })} className="border-zinc-300 rounded-md" data-testid="product-cost-input" /></Field>
              <Field label="Packing Cost"><Input type="number" step="0.01" value={form.packing_cost} onChange={(e) => setForm({ ...form, packing_cost: e.target.value })} className="border-zinc-300 rounded-md" data-testid="packing-cost-input" /></Field>
              <Field label="Shipping Cost"><Input type="number" step="0.01" value={form.shipping_cost} onChange={(e) => setForm({ ...form, shipping_cost: e.target.value })} className="border-zinc-300 rounded-md" data-testid="shipping-cost-input" /></Field>
            </div>
            <div className="mt-3 pt-3 border-t border-zinc-200 flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Total price</p>
              <div className="flex items-center gap-3">
                {isEdit && delta !== 0 && (
                  <span className={`inline-flex items-center gap-1 text-xs font-semibold ${delta > 0 ? "text-red-700" : "text-emerald-700"}`}>
                    {delta > 0 ? <TrendUp size={12} weight="bold" /> : <TrendDown size={12} weight="bold" />}
                    {delta > 0 ? "+" : ""}{delta.toFixed(2)} {form.currency}
                  </span>
                )}
                <p className="text-3xl font-black tracking-tighter font-mono" data-testid="product-total-preview">
                  {formatCurrency(total, form.currency)}
                </p>
              </div>
            </div>
          </div>

          <div className="border border-zinc-200 bg-blue-50/30 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Ticket size={14} weight="bold" className="text-blue-700" />
                <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Price Tiers (bulk discount)</p>
              </div>
              <button
                type="button"
                onClick={() => setForm({ ...form, price_tiers: [...form.price_tiers, { min_qty: 1, unit_price: form.product_cost + form.packing_cost + form.shipping_cost || 0 }] })}
                className="text-[10px] uppercase tracking-widest text-blue-700 hover:text-blue-900 font-bold flex items-center gap-1"
                data-testid="add-tier-button"
              >
                <Plus size={12} weight="bold" /> Add tier
              </button>
            </div>
            {form.price_tiers.length === 0 ? (
              <p className="text-xs text-zinc-500">No tiers set. Total price ({formatCurrency(total, form.currency)}) applies to all quantities.</p>
            ) : (
              <div className="space-y-2" data-testid="price-tiers-list">
                {form.price_tiers.map((t, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`tier-row-${i}`}>
                    <span className="text-xs text-zinc-600 whitespace-nowrap">Qty ≥</span>
                    <Input
                      type="number" min="1" value={t.min_qty}
                      onChange={(e) => {
                        const copy = [...form.price_tiers];
                        copy[i] = { ...copy[i], min_qty: e.target.value };
                        setForm({ ...form, price_tiers: copy });
                      }}
                      className="border-zinc-300 rounded-md h-8 w-24"
                      data-testid={`tier-min-qty-${i}`}
                    />
                    <span className="text-xs text-zinc-600 whitespace-nowrap">→ unit price</span>
                    <Input
                      type="number" step="0.01" value={t.unit_price}
                      onChange={(e) => {
                        const copy = [...form.price_tiers];
                        copy[i] = { ...copy[i], unit_price: e.target.value };
                        setForm({ ...form, price_tiers: copy });
                      }}
                      className="border-zinc-300 rounded-md h-8 flex-1"
                      data-testid={`tier-unit-price-${i}`}
                    />
                    <span className="text-xs text-zinc-500 whitespace-nowrap">{form.currency}</span>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, price_tiers: form.price_tiers.filter((_, j) => j !== i) })}
                      className="p-1 hover:bg-red-50 text-zinc-400 hover:text-red-600 rounded"
                      data-testid={`remove-tier-${i}`}
                    >
                      <XIcon size={12} weight="bold" />
                    </button>
                  </div>
                ))}
                <p className="text-[10px] text-zinc-500 mt-2">Tiers are applied top-down: the highest tier whose min-qty ≤ ordered qty wins.</p>
              </div>
            )}
          </div>

          <div className="border border-zinc-200 bg-emerald-50/30 p-4">
            <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">Bulk Promotion (optional)</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Threshold quantity"><Input type="number" min="0" placeholder="10" value={form.bonus_threshold} onChange={(e) => setForm({ ...form, bonus_threshold: e.target.value })} className="border-zinc-300 rounded-md" data-testid="bonus-threshold-input" /></Field>
              <Field label="Free bonus per threshold"><Input type="number" min="0" placeholder="1" value={form.bonus_quantity} onChange={(e) => setForm({ ...form, bonus_quantity: e.target.value })} className="border-zinc-300 rounded-md" data-testid="bonus-quantity-input" /></Field>
            </div>
            {bonusEnabled ? (
              <p className="text-xs text-emerald-800 mt-2" data-testid="bonus-preview">
                Every <strong>{form.bonus_threshold}</strong> bags ordered → <strong>+{form.bonus_quantity}</strong> free bag(s) shipped.
                Example: order 10 → ship {10 + Math.floor(10 / (parseInt(form.bonus_threshold, 10) || 1)) * (parseInt(form.bonus_quantity, 10) || 0)}.
              </p>
            ) : (
              <p className="text-xs text-zinc-500 mt-2">Set both fields &gt; 0 to enable a bulk promotion.</p>
            )}
          </div>

          {isEdit && (
            <Field label="Reason for change (optional but recommended)">
              <Input placeholder="Market rate went up, new supplier, …" value={form.change_note} onChange={(e) => setForm({ ...form, change_note: e.target.value })} className="border-zinc-300 rounded-md" data-testid="change-note-input" />
            </Field>
          )}

          <Field label="Internal Notes">
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border-zinc-300 rounded-md" data-testid="product-notes-input" />
          </Field>

          <div className="border border-zinc-200 bg-zinc-50 p-4">
            <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">Product Image</p>
            {form.image_file_id ? (
              <div className="flex items-center gap-3 mb-3">
                <img
                  src={fileUrl(`/api/storage/files/${form.image_file_id}`)}
                  alt="Product"
                  className="w-24 h-24 object-cover border border-zinc-200 rounded"
                  data-testid="product-image-preview"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setForm({ ...form, image_file_id: null })}
                  className="border-zinc-300 rounded-md"
                  data-testid="remove-product-image"
                >
                  Remove
                </Button>
              </div>
            ) : null}
            <FileUploader
              kind="product_image"
              refType="product"
              refId={product?.product_id || null}
              accept="image/*"
              label="Upload image (JPG/PNG/WebP · max 10 MB)"
              testId="product-image-uploader"
              onUploaded={(data) => setForm((f) => ({ ...f, image_file_id: data.file_id }))}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
            <Button type="button" variant="outline" onClick={onClose} className="border-zinc-300 rounded-md">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="save-product-button">
              {busy ? "Saving…" : (isEdit ? "Save Changes" : "Create Product")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ product, onClose }) {
  const [history, setHistory] = useState([]);
  useEffect(() => {
    api.get(`/products/${product.product_id}/history`).then((r) => setHistory(r.data));
  }, [product.product_id]);

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl bg-white max-h-[80vh] overflow-y-auto" data-testid="price-history-dialog">
        <DialogHeader><DialogTitle>Price History · {product.name}</DialogTitle></DialogHeader>
        <div className="space-y-3" data-testid="history-list">
          {history.map((h) => {
            const prev = h.previous;
            const now = h.new;
            const delta = prev ? now.total_price - prev.total_price : 0;
            return (
              <div key={h.history_id} className="border border-zinc-200 p-3" data-testid={`history-entry-${h.history_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">{formatDateTime(h.changed_at)}</p>
                    <p className="text-xs text-zinc-700">by {h.changed_by}</p>
                  </div>
                  {prev ? (
                    <span className={`inline-flex items-center gap-1 text-sm font-mono font-bold ${delta > 0 ? "text-red-700" : delta < 0 ? "text-emerald-700" : "text-zinc-500"}`}>
                      {delta > 0 && <TrendUp size={14} weight="bold" />}
                      {delta < 0 && <TrendDown size={14} weight="bold" />}
                      {formatCurrency(prev.total_price, prev.currency)} → {formatCurrency(now.total_price, now.currency)}
                    </span>
                  ) : (
                    <span className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Created @ {formatCurrency(now.total_price, now.currency)}</span>
                  )}
                </div>
                {prev && (
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <PricePair label="Product" prev={prev.product_cost} now={now.product_cost} currency={now.currency} />
                    <PricePair label="Packing" prev={prev.packing_cost} now={now.packing_cost} currency={now.currency} />
                    <PricePair label="Shipping" prev={prev.shipping_cost} now={now.shipping_cost} currency={now.currency} />
                  </div>
                )}
                {h.note && <p className="text-xs italic text-zinc-600 mt-2 pt-2 border-t border-zinc-100">"{h.note}"</p>}
              </div>
            );
          })}
          {history.length === 0 && <p className="p-6 text-center text-sm text-zinc-500">No history yet.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PricePair({ label, prev, now, currency }) {
  const changed = prev !== now;
  return (
    <div className={`px-2 py-1 border ${changed ? "border-amber-300 bg-amber-50" : "border-zinc-200 bg-zinc-50"}`}>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</p>
      <p className="font-mono">
        {changed && <span className="line-through text-zinc-400">{formatCurrency(prev, currency)}</span>}
        {changed && " → "}
        <span className="font-semibold">{formatCurrency(now, currency)}</span>
      </p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
